import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { NAVY, RADIUS_ITEM } from 'styles/tokens';

const FONT = 'Jost, sans-serif';

interface PaginationFooterProps {
  page: number;
  pageSize: number;
  rowCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  sx?: SxProps<Theme>;
}

const PaginationFooter: FC<PaginationFooterProps> = ({
  page,
  pageSize,
  rowCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  sx,
}) => {
  const totalPages = Math.ceil(rowCount / pageSize);
  const rangeStart = page * pageSize + 1;
  const rangeEnd = Math.min((page + 1) * pageSize, rowCount);

  const navButtonSx = {
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '0.75rem',
    fontFamily: FONT,
    color: alpha(NAVY, 0.8),
    minWidth: 0,
    px: 0.75,
    borderRadius: RADIUS_ITEM,
    transition: 'background-color 0.15s ease-in-out',
    '&:hover:not(.Mui-disabled)': {
      bgcolor: alpha(NAVY, 0.06),
    },
    '&.Mui-disabled': { color: `${NAVY}40` },
  };

  return (
    <>
      {/* Reserve space on mobile so content isn't hidden behind the fixed bar */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, height: 42, flexShrink: 0 }} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 1,
          px: { xs: 1, md: 2 },
          py: 1,
          mt: { xs: 0, md: 0.5 },
          position: { xs: 'fixed', md: 'static' },
          bottom: { xs: 0, md: 'auto' },
          left: { xs: 0, md: 'auto' },
          right: { xs: 0, md: 'auto' },
          zIndex: { xs: 10, md: 'auto' },
          ...sx,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            size="small"
            sx={{
              fontFamily: FONT,
              fontSize: '0.75rem',
              fontWeight: 500,
              color: NAVY,
              height: 26,
              borderRadius: '6px',
              '& .MuiSelect-select': { py: 0, px: '8px', lineHeight: '26px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: `${NAVY}20` },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: `${NAVY}40` },
              '& .MuiSelect-icon': { fontSize: '1rem', color: `${NAVY}80` },
            }}
          >
            {pageSizeOptions.map((size) => (
              <MenuItem key={size} value={size} sx={{ fontFamily: FONT, fontSize: '0.75rem' }}>
                {size}
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ fontSize: '0.75rem', color: `${NAVY}80`, fontFamily: FONT }}>
            {rangeStart}–{rangeEnd} de {rowCount}
          </Typography>
          <Button size="small" disabled={page === 0} onClick={() => onPageChange(page - 1)} sx={navButtonSx}>
            Anterior
          </Button>
          <Button
            size="small"
            disabled={page >= totalPages - 1}
            onClick={() => onPageChange(page + 1)}
            sx={navButtonSx}
          >
            Siguiente
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default PaginationFooter;
